import { authState } from "@/plugins/keycloak";
import { logger } from "../services/LoggerService";
/**
 * Keycloak implementation of the Password Service.
 * Handles password change operations via Keycloak's UPDATE_PASSWORD action.
 *
 * Follows opay's approach: uses query parameters instead of sessionStorage
 * for simpler, more reliable password change flow detection.
 */
export class KeycloakPasswordService {
    /**
     * Initiates password change by redirecting to Keycloak with UPDATE_PASSWORD action.
     * Also tracks analytics event automatically.
     *
     * Why we use login() with action instead of direct URL:
     * - Maintains session integrity
     * - Handles PKCE/state automatically
     * - Respects existing authentication scope
     */
    async initiatePasswordChange(options) {
        const { redirectUri, locale } = options;
        logger.debug("Initiating password change", { redirectUri, locale });
        // Track analytics event before redirect
        this.trackPasswordChangeRequested();
        try {
            // Use Keycloak's login with UPDATE_PASSWORD required action
            // This is the recommended approach for authenticated users
            // Append query param to detect successful return (opay pattern)
            const redirectWithParam = `${redirectUri}?passwordChanged=true`;
            await authState.keycloakInstance.login({
                action: "UPDATE_PASSWORD",
                redirectUri: redirectWithParam,
                locale,
                // Note: We intentionally do NOT pass 'scope' here
                // to reuse the existing session scope
            });
        }
        catch (error) {
            logger.debug("Password change redirect failed:", error);
            throw error;
        }
    }
    /**
     * Checks if a password change was recently initiated.
     * Uses query parameters from URL (opay approach).
     */
    wasPasswordChangeInitiated() {
        const urlParams = new URLSearchParams(window.location.search);
        const fromKeycloak = urlParams.get("passwordChanged") === "true";
        if (!fromKeycloak) {
            return false; // browser back or refresh → no toast
        }
        // Remove the query param so it won't trigger toast again (opay pattern)
        window.history.replaceState({}, "", window.location.pathname);
        return true;
    }
    /**
     * Clears the password change flag.
     * With query parameter approach, this is handled by replaceState in wasPasswordChangeInitiated.
     * Kept for interface compatibility.
     */
    clearPasswordChangeFlag() {
        // Query parameter is already cleared in wasPasswordChangeInitiated()
        // This method is kept for interface compatibility
        logger.debug("clearPasswordChangeFlag called (no-op with query parameter approach)");
    }
    /**
     * Tracks the password change request event for analytics.
     * This is called automatically within initiatePasswordChange().
     */
    trackPasswordChangeRequested() {
        try {
            const tokenParsed = authState.keycloakInstance.tokenParsed;
            const analyticsPayload = {
                event: "password_change_requested",
                user_id: tokenParsed?.sub || "unknown",
                organisation_id: tokenParsed?.organisation_id || tokenParsed?.azp || "unknown",
                timestamp: new Date().toISOString(),
            };
            logger.debug("Analytics event:", analyticsPayload);
            // TODO: Inject analytics service when available
            // analyticsService.track(analyticsPayload);
        }
        catch (error) {
            logger.debug("Failed to track analytics event:", error);
        }
    }
}
// Singleton instance
export const keycloakPasswordService = new KeycloakPasswordService();
